import { FormikProps, withFormik } from 'formik'
import type { TFunction } from 'i18next'
import React from 'react'
import { Trans } from 'react-i18next'

import { ApiErrorBody } from '@app/routing/Session/ErrorResponse'

import { ChangeTradingAccountPasswordValues } from '@pages/Traders-Room/Trading-Accounts/ChangeTradingAccountPassword/ChangeTradingAccountPassword.page'

import { Body1, Heading6 } from '@ui/Typography'
import { Link } from '@ui/components/Link'

import { FormSubmitValues } from '@utils/formValidation'
import { maskEmail } from '@utils/maskEmail'

import { AccountDetailedDto } from '@model/AccountDetailedDto'

import { Box } from '@shared/components/Box'
import { VerificationCodeInput } from '@shared/components/VerificationCodeInput/VerificationCodeInput'

import styles from './ChangeTradingAccountPasswordVerify.module.scss'

export interface CodeFormValues {
  code: string
  code1: string
  code2: string
  code3: string
  code4: string
  code5: string
  code6: string
}

const fieldKeys: (keyof CodeFormValues)[] = ['code1', 'code2', 'code3', 'code4', 'code5', 'code6']

type ChangeTradingAccountPasswordVerifyFormProps = React.FunctionComponent<
  FormikProps<CodeFormValues> & OuterProps
>

export const ChangeTradingAccountPasswordVerifyFormUI: ChangeTradingAccountPasswordVerifyFormProps =
  (props) => {
    const { t, values, isSubmitting, setFieldValue, handleSubmit, onPasswordRestSubmit } = props
    const { account, validationError } = props

    const email = account?.emails[0]?.address || 'x'

    const handleCodeChange = (code: string) => {
      setFieldValue('code', code)
    }

    const handleCodeComplete = async () => {
      if (!isSubmitting) {
        await handleSubmit()
      }
    }

    const handleResend = async () => {
      await onPasswordRestSubmit()
      setFieldValue('code', '')
    }

    return (
      <>
        <Heading6 medium>{t(`Trading Account.Confirm it's you`)}</Heading6>
        <Body1 color='text-low-em'>
          <Trans
            i18nKey='Trading Account.Enter the verification code sent to email'
            values={{ email: maskEmail(email) }}
          />
        </Body1>
        <Box flex align='center' fullWidth justify='center' gap='x4'>
          <VerificationCodeInput
            length={6}
            value={values.code}
            onChange={handleCodeChange}
            onComplete={handleCodeComplete}
            error={!!validationError}
            disabled={isSubmitting}
          />
        </Box>
        {validationError && (
          <Body1 color='text-danger-high-em'>
            {validationError.properties?.RemainingCodeAttemptsCount
              ? t('Trading Account.Wrong validation code', {
                  count: Number(validationError.properties.RemainingCodeAttemptsCount),
                })
              : validationError.message?.message?.includes('Errorcode: 30003')
              ? t('Trading Account.Trading Account Password Used Recently')
              : validationError.message?.message || validationError.message}
          </Body1>
        )}

        <Body1 color='text-low-em' className={styles.text}>
          {t('Sign up.Not get code')}
          <Link variant='tertiary' size='lg' onClick={handleResend}>
            {t('Sign up.Resend')}
          </Link>
        </Body1>
      </>
    )
  }

interface OuterProps {
  t: TFunction
  validationError: ApiErrorBody | null
  tradingAccountId: string
  account?: AccountDetailedDto
  onPasswordRestSubmit(values?: ChangeTradingAccountPasswordValues): Promise<void>
  onSubmit(values: FormSubmitValues<string>): Promise<void>
}

export const ChangeTradingAccountPasswordVerifyForm = withFormik<OuterProps, CodeFormValues>({
  mapPropsToValues: () => {
    return {
      code: '',
      code1: '',
      code2: '',
      code3: '',
      code4: '',
      code5: '',
      code6: '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting, setFieldValue }) => {
    try {
      await props.onSubmit(values.code)
    } catch {
      fieldKeys.forEach((fk) => setFieldValue(fk, ''))
      document.getElementById(fieldKeys[0])?.focus()
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
})(ChangeTradingAccountPasswordVerifyFormUI)
